import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LANGUAGES = [
	{ code: 'en', label: 'English', flag: 'EN' },
	{ code: 'pt', label: 'Portugues', flag: 'PT' },
	{ code: 'es', label: 'Espanol', flag: 'ES' },
	{ code: 'fr', label: 'Francais', flag: 'FR' },
	{ code: 'de', label: 'Deutsch', flag: 'DE' },
	{ code: 'pl', label: 'Polski', flag: 'PL' },
];

export default function AuthPage() {
  const { t, i18n } = useTranslation();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
		email: '',
		password: '',
		name: '',
		phone: '',
		lang: i18n.language
	});
	const [acceptTerms, setAcceptTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
		if (!isLogin && !acceptTerms) {
			toast.error(t('auth.terms_required'));
			return;
		}
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success(t('auth.login_title') );
      } else {
				await register({ ...form, accept_terms: true });
        toast.success(t('auth.account_created'));
      }
      navigate(searchParams.get('redirect') || '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.authentication_failed'));
    }
    setLoading(false);
  };

	const selectLanguage = (langCode) => {
		setForm({...form, lang: langCode})
	}

  return (
    <div className="pt-20 md:pt-24 min-h-screen flex items-center justify-center px-4"
				 data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-2">
            {isLogin ? t('auth.login_title') : t('auth.register_title')}
          </h1>
          <p className="text-[#666055]">
            {isLogin ? t('auth.login_subtitle') : t('auth.register_subtitle')}
          </p>
        </div>

        <div className="bg-white border border-[#E6E2D6] rounded-sm p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <Label
										className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('auth.name')}
									</Label>
                  <Input
                    data-testid="auth-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="mt-1.5 bg-[#F9F7F2] border-[#E6E2D6] focus:border-[#1A3C34] rounded-sm h-12"
                    placeholder={ t('auth.name_placeholder') }
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('auth.phone')}
									</Label>
                  <Input
                    data-testid="auth-phone"
                    value={form.phone}
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    className="mt-1.5 bg-[#F9F7F2] border-[#E6E2D6] focus:border-[#1A3C34] rounded-sm h-12"
                    placeholder="+351 912 345 678"
                  />
                </div>
								<div>
									<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('admin.faq_languages')}
									</Label>
									{/* Language Selector */}
									<Select value={form.lang} onValueChange={ selectLanguage }>
										<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
											<SelectValue placeholder="lang" />
										</SelectTrigger>
										<SelectContent>
											{ LANGUAGES.map(l =>
												<SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)
											}
										</SelectContent>
									</Select>
								</div>
              </>
            )}
            <div>
              <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{t('auth.email')}
							</Label>
              <Input
                data-testid="auth-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                className="mt-1.5 bg-[#F9F7F2] border-[#E6E2D6] focus:border-[#1A3C34] rounded-sm h-12"
                placeholder={t('auth.email_placeholder')}
              />
            </div>
            <div>
							<div className="flex items-center justify-between">
								<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('auth.password')}
								</Label>
								{isLogin && (
									<Link
										to="/forgot-password"
										className="text-[11px] text-[#8B4513] hover:underline"
										data-testid="auth-forgot-link"
									>
										{t('auth.forgot_password')}
									</Link>
								)}
							</div>
              <Input
                data-testid="auth-password"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
                className="mt-1.5 bg-[#F9F7F2] border-[#E6E2D6] focus:border-[#1A3C34] rounded-sm h-12"
                placeholder={t('auth.password_placeholder')}
              />
            </div>
						{!isLogin && (
							<div className="flex items-start gap-3 pt-1" data-testid="auth-terms-row">
								<Checkbox
									id="accept-terms"
									checked={acceptTerms}
									onCheckedChange={(v) => setAcceptTerms(!!v)}
									className="mt-0.5"
									data-testid="auth-accept-terms"
								/>
								<label
									htmlFor="accept-terms"
									className="text-xs leading-relaxed text-[#3a3a3a] cursor-pointer select-none"
								>
									<Trans
										i18nKey="auth.accept_terms"
										components={{
											terms: (
												<Link
													to="/terms"
													target="_blank"
													rel="noopener noreferrer"
													className="text-[#8B4513] underline hover:text-[#6B3410]"
													data-testid="auth-terms-link"
												/>
											),
											privacy: (
												<Link
													to="/privacy"
													target="_blank"
													rel="noopener noreferrer"
													className="text-[#8B4513] underline hover:text-[#6B3410]"
													data-testid="auth-privacy-link"
												/>
											),
										}}
									/>
								</label>
							</div>
						)}

            <Button
              data-testid="auth-submit"
              type="submit"
							disabled={loading || (!isLogin && !acceptTerms)}
              className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-12 text-base font-medium btn-equi"
            >
              {loading ? t('common.loading') : isLogin ? t('auth.login_btn') : t('auth.register_btn')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="auth-toggle"
							onClick={() => { setIsLogin(!isLogin); setAcceptTerms(false); }}
              className="text-sm text-[#8B4513] hover:underline">
              {isLogin ? t('auth.no_account') : t('auth.have_account')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
